import { defineStore } from 'pinia'
import { ref } from 'vue'
// @ts-expect-error: vuex
import { useStore } from 'vuex'
import dayjs from 'dayjs'
import formats from '@/constants/momentFormats'
import isString from 'lodash.isstring'

import donorContactService from '@/services/http/api/donorContact'
import resourceService from '@/services/http/api/resource'
import {
  shouldRetryGetActionForAssignedEndpoint,
  shouldRetryPostActionForAssignedEndpoint
} from '@/helpers/retryActionForOtherEndpoint'

import type { Maybe, TODO } from '@/types'

const emptyDonorContactForm = (): TODO => ({
  contactDate: null,
  contactType: null,
  contactDescription: null
})

export const useDonorContactStore = defineStore('donorContact', () => {
  const loader = ref<boolean>(false)
  const sendLoader = ref<boolean>(false)
  const donorContact = ref<Maybe<TODO>>(null)
  const resource = ref<Maybe<TODO>>(null)
  const donorContactForm = ref(emptyDonorContactForm())
  const editMode = ref<boolean>(false)

  const vuexStore = useStore()

  const clear = () => {
    loader.value = false
    donorContact.value = null
    editMode.value = false
    resource.value = null
  }

  const clearDonorContactForm = () => {
    donorContactForm.value = emptyDonorContactForm()
  }

  const fetchResource = async (payload: string) => {
    try {
      const { data } = await resourceService.donorContact(payload)
      resource.value = data.data
    } catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchDonorContact = async (payload: TODO) => {
    try {
      const { data } = await donorContactService.get(payload, payload.getAssignedFirst)
      const responseData = data?.data
      if (typeof responseData?.content?.contactDate === 'object') {
        responseData.content.contactDate = dayjs(responseData.content.contactDate?.date).format(
          formats.FULL_DATE
        )
      }
      donorContact.value = responseData
      donorContactForm.value = responseData?.content
      loader.value = false
      return responseData
    } catch (error) {
      if (shouldRetryGetActionForAssignedEndpoint(error, payload)) {
        await fetchDonorContact({ ...payload, getAssignedFirst: true })
      } else throw error
    }
  }

  const init = async (payload: TODO) => {
    clear()
    clearDonorContactForm()

    loader.value = true
    let editionName
    try {
      editionName = await vuexStore.dispatch(
        'childEdition/getChildsEditionName',
        { ...payload },
        { root: true }
      )
    } catch (error) {
      return Promise.reject(error)
    }

    if (!isString(editionName) || editionName.length === 0) return null

    await fetchResource(editionName)

    if (payload.routeName !== 'DonorContact') {
      editMode.value = true
      loader.value = false
      return null
    }

    try {
      await fetchDonorContact(payload)
    } catch {
      loader.value = false
    }
    return null
  }

  const responseFunction = (data: TODO) => {
    const { school_hydra_users: schoolHydraUsers } =
      vuexStore.getters['childEdition/getChild'] ?? {}

    const childEdition = data?.data?.child_edition

    if (childEdition && typeof childEdition === 'object') {
      vuexStore.commit(
        'childEdition/setChild',
        {
          child_edition: childEdition,
          school_hydra_users: schoolHydraUsers
        },
        { root: true }
      )
    }
    vuexStore.commit('childEdition/setChildEditionRequestSkipped', true, { root: true })
    return data
  }

  const send = async (payload: TODO = {}) => {
    try {
      sendLoader.value = true
      const { data } = await donorContactService.post(payload, payload.postAssignedFirst)
      return responseFunction(data)
    } catch (error) {
      if (shouldRetryPostActionForAssignedEndpoint(error, payload)) {
        await send({ ...payload, postAssignedFirst: true })
      } else throw error
    } finally {
      sendLoader.value = false
    }
  }

  return {
    // state
    loader,
    sendLoader,
    donorContact,
    resource,
    donorContactForm,
    editMode,
    // getters
    // actions
    clear,
    clearDonorContactForm,
    fetchResource,
    fetchDonorContact,
    init,
    send
  }
})
